import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Container,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Backdrop,
  Snackbar
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { 
  getEnhancedDashboardSummary, 
  getRecentActivities, 
  getCostsOverview, 
  getStatusOverview,
  getGeographicDistribution,
  getSupplierDistribution,
  getClientCompletenessScores,
  getContractExpiryForecast,
  getClientRevenueAnalysis,
  getServicePenetrationAnalysis,
  getContractValueDistribution
} from '../../api/utilityServices';
import DashboardFilters, { FilterOptions } from './components/DashboardFilters';
import { DashboardStats } from './components/DashboardStats';
import { ServiceTypeDistribution } from './components/ServiceTypeDistribution';
import { ContractValueDistribution } from './components/ContractValueDistribution';
import { ContractRenewalTimeline } from './components/ContractRenewalTimeline';
import { RecentActivities } from './components/RecentActivities';
import BusinessTypeDistribution from './components/BusinessTypeDistribution';
import IndustrySectorDistribution from './components/IndustrySectorDistribution';
import ClientAcquisitionTimeline from './components/ClientAcquisitionTimeline';
import ContractExpiryForecast from './components/ContractExpiryForecast';
import ClientCompleteness from './components/ClientCompleteness';
import { ClientRevenueAnalysis } from './components/ClientRevenueAnalysis';
import { GeographicDistribution as GeographicDistributionComponent } from './components/GeographicDistribution';
import { ServicePenetrationAnalysis } from './components/ServicePenetrationAnalysis';
import DataQualityOverview from './components/DataQualityOverview';
import { QuoteConversionMetricsComponent } from '../../components/Dashboard/QuoteConversionMetrics';
import { 
  EnhancedDashboardSummary, 
  Activity, 
  CostOverview, 
  StatusOverview,
  GeographicDistribution,
  SupplierDistribution,
  ClientCompletenessScore,
  ContractExpiryForecast as ContractExpiryForecastType,
  ServiceTypeCount,
  UtilityService
} from '../../types/utility.types';
import { StatusOverviewChart } from './components/StatusOverviewChart';
import { CostOverviewChart } from './components/CostOverviewChart';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `dashboard-tab-${index}`,
    'aria-controls': `dashboard-tabpanel-${index}`,
  };
}

const UtilityDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<EnhancedDashboardSummary | null>(null);
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
  const [costsOverview, setCostsOverview] = useState<CostOverview[]>([]);
  const [statusOverview, setStatusOverview] = useState<StatusOverview[]>([]);
  const [geographicDistribution, setGeographicDistribution] = useState<GeographicDistribution[]>([]);
  const [supplierDistribution, setSupplierDistribution] = useState<SupplierDistribution[]>([]);
  const [clientCompleteness, setClientCompleteness] = useState<ClientCompletenessScore[]>([]);
  const [contractExpiryForecast, setContractExpiryForecast] = useState<ContractExpiryForecastType[]>([]);
  const [clientRevenueData, setClientRevenueData] = useState<any[]>([]);
  const [servicePenetrationData, setServicePenetrationData] = useState<any>(null);
  const [contractValueData, setContractValueData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [filters, setFilters] = useState<FilterOptions>({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 3)),
    endDate: new Date(),
    serviceTypes: []
  });

  // Available service types for filtering
  const availableServiceTypes = useMemo(() => [
    'Gas', 'Electricity', 'Water', 'Telecoms', 'Waste', 
    'Merchant Services', 'PPA Export Contract', 'EV', 'Renewable', 'Insurance'
  ], []);

  const handleFilterChange = useCallback((newFilters: FilterOptions) => {
    setFilters(newFilters);
    fetchDashboardData(true, newFilters);
  }, []);

  const fetchDashboardData = useCallback(async (isRefresh = false, filterOptions = filters) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Prepare filter parameters
      const params: Record<string, string> = {};
      if (filterOptions.startDate) {
        params.startDate = filterOptions.startDate.toISOString();
      }
      if (filterOptions.endDate) {
        params.endDate = filterOptions.endDate.toISOString();
      }
      if (filterOptions.serviceTypes.length > 0) {
        params.serviceTypes = filterOptions.serviceTypes.join(',');
      }

      // Call APIs with filter parameters
      const summary = await getEnhancedDashboardSummary(params);
      const activities = await getRecentActivities(params);
      const costs = await getCostsOverview(params);
      const status = await getStatusOverview(params);
      const clientRevenue = await getClientRevenueAnalysis(params);
      const servicePenetration = await getServicePenetrationAnalysis();
      const geographic = await getGeographicDistribution(params);
      const supplier = await getSupplierDistribution(params);
      const completeness = await getClientCompletenessScores(params);
      const contractExpiry = await getContractExpiryForecast(params);
      const contractValue = await getContractValueDistribution(params);

      setDashboardData(summary);
      setRecentActivities(activities);
      setCostsOverview(costs);
      setStatusOverview(status);
      setClientRevenueData(clientRevenue);
      setServicePenetrationData(servicePenetration);
      setGeographicDistribution(geographic);
      setSupplierDistribution(supplier);
      setClientCompleteness(completeness);
      setContractExpiryForecast(contractExpiry);
      setContractValueData(contractValue);
      
      if (isRefresh) {
        setSnackbarMessage('Dashboard data refreshed successfully');
        setSnackbarOpen(true);
      }
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load dashboard data';
      setError(`${errorMessage}. Please try again later.`);
      
      if (isRefresh) {
        setSnackbarMessage('Failed to refresh dashboard data');
        setSnackbarOpen(true);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters]);

  const handleRefresh = useCallback(() => {
    fetchDashboardData(true, filters);
  }, [fetchDashboardData, filters]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading && !refreshing) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading Dashboard Data
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error && !refreshing) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert 
          severity="error" 
          variant="filled"
          action={
            <Button color="inherit" size="small" onClick={() => fetchDashboardData()}>
              Retry
            </Button>
          }
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
        <Card sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          color: 'white'
        }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ color: 'white' }}>Dashboard Error</Typography>
            <Typography variant="body1" paragraph>
              We encountered an issue while loading the dashboard data. This could be due to:
            </Typography>
            <ul>
              <li>Network connectivity issues</li>
              <li>Server maintenance</li>
              <li>Temporary service disruption</li>
            </ul>
            <Box sx={{ mt: 2 }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => fetchDashboardData()}
                startIcon={<RefreshIcon />}
              >
                Reload Dashboard
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={refreshing}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 2 }}>Refreshing dashboard data...</Typography>
        </Box>
      </Backdrop>
      
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h4" component="h1">
                Utility Dashboard
              </Typography>
              <Tooltip title="Refresh dashboard data">
                <IconButton onClick={handleRefresh} disabled={refreshing}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
            
            <DashboardFilters 
              onFilterChange={handleFilterChange}
              serviceTypes={availableServiceTypes}
            />
            <Paper sx={{ 
              p: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              color: 'white'
            }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                aria-label="dashboard tabs"
              >
                <Tab label="Service Overview" {...a11yProps(0)} />
                <Tab label="Client Insights" {...a11yProps(1)} />
                <Tab label="Contract Analysis" {...a11yProps(2)} />
                <Tab label="Quote Analytics" {...a11yProps(3)} />
                <Tab label="Data Quality" {...a11yProps(4)} />
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <ServiceTypeDistribution 
                      data={dashboardData?.servicesByType || {
                        ELECTRICITY: 0,
                        GAS: 0,
                        WATER: 0,
                        TELECOMS: 0,
                        WASTE: 0,
                        MERCHANT_SERVICES: 0,
                        PPA_EXPORT_CONTRACT: 0,
                        EV: 0,
                        RENEWABLE: 0,
                        INSURANCE: 0,
                        NEW_CONNECTION: 0
                      } as ServiceTypeCount}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(4px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      color: 'white'
                    }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                          Status Overview
                        </Typography>
                        {statusOverview.length > 0 ? (
                          <StatusOverviewChart data={statusOverview} />
                        ) : (
                          <Typography>No status data available</Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(4px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      color: 'white'
                    }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                          Cost Overview
                        </Typography>
                        {costsOverview.length > 0 ? (
                          <CostOverviewChart data={costsOverview} />
                        ) : (
                          <Typography>No cost data available</Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ContractValueDistribution data={contractValueData} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ContractRenewalTimeline 
                      services={(dashboardData?.contractsEndingSoon || []) as unknown as UtilityService[]}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <ClientRevenueAnalysis 
                      data={clientRevenueData}
                      isLoading={loading}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <GeographicDistributionComponent 
                      data={geographicDistribution}
                      isLoading={loading}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ServicePenetrationAnalysis 
                      data={servicePenetrationData}
                      isLoading={loading}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <BusinessTypeDistribution 
                      data={dashboardData?.businessTypeDistribution || []}
                      isLoading={loading}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <IndustrySectorDistribution 
                      data={dashboardData?.industrySectorDistribution || []}
                      isLoading={loading}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ClientAcquisitionTimeline 
                      data={dashboardData?.clientAcquisitionTimeline || []}
                      isLoading={loading}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ClientCompleteness 
                      data={clientCompleteness}
                      isLoading={loading}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <ContractExpiryForecast 
                      data={contractExpiryForecast}
                      isLoading={loading}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <QuoteConversionMetricsComponent 
                      loading={loading}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={4}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <DataQualityOverview 
                      isLoading={loading}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

            </Paper>
          </Grid>
        </Grid>
    </Container>
  );
};

export default UtilityDashboard;
